import { useLayoutEffect, useMemo, useRef, useState, type RefObject } from 'react';

import { funnel } from 'remeda';

function readClientBox(element: HTMLDivElement) {
  return {
    width: element.clientWidth,
    height: element.clientHeight,
  };
}

export const useDimensions = (ref: RefObject<HTMLDivElement | null>, waitMs: number = 1000) => {
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  // Read during render so dependency tracking follows the real node, not the ref object
  // (which is stable when the attached element is swapped, e.g. conditional ref targets).
  const element = ref.current;

  const debouncedSetDimensions = useMemo(
    () =>
      funnel(
        () => {
          if (ref?.current) {
            setDimensions(readClientBox(ref.current));
          }
        },
        { minQuietPeriodMs: waitMs }
      ),
    [ref, waitMs]
  );

  const debouncedRef = useRef(debouncedSetDimensions);
  debouncedRef.current = debouncedSetDimensions;

  const [elementObserver] = useState(
    () =>
      new ResizeObserver(() => {
        debouncedRef.current.call();
      })
  );

  useLayoutEffect(() => {
    // Refs are set before this effect. The render-time `element` is still null on the
    // first mount, so reading it here would skip the node until a later render.
    const node = ref.current ?? element;
    if (!node) {
      setDimensions(prev =>
        prev.width === 0 && prev.height === 0 ? prev : { width: 0, height: 0 }
      );
      return () => {
        debouncedSetDimensions.cancel();
      };
    }

    setDimensions(readClientBox(node));
    elementObserver.observe(node);
    return () => {
      debouncedSetDimensions.cancel();
      elementObserver.unobserve(node);
    };
  }, [debouncedSetDimensions, element, elementObserver, ref]);

  return dimensions;
};
